import React from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Phone,
  MessageCircle,
  MapPin,
  Mail,
  ArrowRight,
  Award,
  Users,
  Target,
  Heart
} from 'lucide-react';
import { companyInfo, stats } from '../mock';
import logo from '../assets/logo.png'; // ✅ LOCAL IMAGE ONLY

const About = () => {
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/91${companyInfo.whatsapp}?text=Hi, I want to know more about UNITYPRO HOMES`,
      '_blank'
    );
  };

  const handleCall = () => {
    window.location.href = `tel:+91${companyInfo.phones[0]}`;
  };

  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "Committed to delivering the highest quality properties and services to our clients"
    },
    {
      icon: Users,
      title: "Trust",
      description: "Building lasting relationships through transparency and honest dealings"
    },
    {
      icon: Target,
      title: "Innovation",
      description: "Constantly evolving to bring you the best investment opportunities"
    },
    {
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction and dreams are at the heart of everything we do"
    }
  ];

  return (
    <div className="about-page min-h-screen bg-black pt-24 pb-20">
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <div className="flex justify-center mb-6">
              {/* ✅ ONLY IMAGE SOURCE CHANGED */}
              <img 
                src={logo}
                alt="UNITYPRO HOMES Logo"
                className="h-28 md:h-36 lg:h-40 w-auto object-contain"
              />
            </div>

            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              About Us
            </Badge>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              <span className="gradient-text">{companyInfo.name}</span>
            </h1>

            <p className="text-xl md:text-2xl text-orange-400 font-semibold mb-3 md:mb-4">
              {companyInfo.slogan}
            </p>

            <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              {companyInfo.tagline}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card text-center">
                <div className="text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Managing Director Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <Card className="md-card overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="md-image-wrapper relative">
                <img 
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&h=800&fit=crop" 
                  alt="Managing Director"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
              </div>

              <div className="p-12 flex flex-col justify-center bg-gradient-to-br from-gray-900 to-black">
                <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50 w-fit">
                  Leadership
                </Badge>

                <h2 className="text-4xl font-bold mb-2 text-white">
                  {companyInfo.managingDirector}
                </h2>

                <p className="text-xl text-orange-400 mb-6">
                  {companyInfo.designation}
                </p>

                <div className="space-y-4 text-gray-300">
                  <p className="text-lg leading-relaxed">
                    With over a decade of experience in the real estate industry, {companyInfo.managingDirector} has been 
                    instrumental in transforming UNITYPRO HOMES into one of Delhi's most trusted property developers.
                  </p>
                  <p className="text-lg leading-relaxed">
                    His vision of creating "खुशियों का नया address" drives the company's commitment to helping 
                    families find their dream homes and make smart property investments.
                  </p>
                  <p className="text-lg leading-relaxed">
                    Under his leadership, UNITYPRO HOMES has successfully delivered 50+ projects and served 
                    over 1000+ happy customers across Delhi and NCR.
                  </p>
                </div>

                <div className="mt-8 flex gap-4">
                  <Button className="cta-button-primary" onClick={handleWhatsApp}>
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Connect on WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
                Our Journey
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">Building Dreams Since 2010</span>
              </h2>
            </div>

            <Card className="story-card p-10">
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  UNITYPRO HOMES was founded with a simple yet powerful vision - to make quality real estate 
                  accessible to every family in Delhi.
                </p>
                <p>
                  Located strategically in Kapashera, Delhi, we specialize in premium residential plots, modern homes, 
                  farmhouses, and commercial properties across Delhi and NCR.
                </p>
                <p>
                  We believe in transparent dealings, clear documentation, and customer satisfaction above all.
                </p>
                <p>
                  Our tagline "खुशियों का नया address" reflects our commitment.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Our Values
            </Badge>

            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="gradient-text">What Drives Us</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="value-card p-8 text-center">
                  <div className="value-icon-wrapper mb-6">
                    <IconComponent className="w-12 h-12" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white">
                    {value.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
